/* eslint-disable react/no-array-index-key */
/* eslint-disable no-param-reassign */
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { ForwardRefRenderFunction, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import icSuperBotX from '../assets/images/png/ic_superbotx_logo.png';
import icSuper8 from '../assets/images/png/s8s_logo.png';
import icAlertTriangle from '../assets/images/svg/ic-alert-triangle.svg';
import { getMessageObject, permitMessageContentType, signPayload } from '../common/Util';
import config from '../common/config';
import createLogger from '../common/logger';
import { mergeMessage, mergeMessages, settlePendingMessages } from '../common/mergeMessages';
import messageType from '../common/messageType';
import { attachReplyLocale } from '../common/replyLocale';
import { createResync } from '../common/resyncMessages';
import { emitOrQueueSocketEvent } from '../common/socketEmitQueue';
import { color, fontSize, s8sColor } from '../common/themes';
import useResponsiveScale from './hooks/useResponsiveScale';
import LivechatService from '../services/LivechatService';
import BotTyping from './BotTyping';
import ChatInput from './ChatInput';
import Circular from './Circular';
import FileReviewer from './FileReviewer';
import IconBack from './IconBack';
import IconExpand from './IconExpand';
import IconX from './IconX';
import ImageDisplay from './ImageDisplay';
import MessageContent from './MessageContent';

const logger = createLogger('chat');

const LEGACY_STATUS_MESSAGE_GROUPS = {
	preparing: [1, 2, 3, 4, 5, 6, 7],
	retrieving: [10, 11],
	thinking: [8, 9, 12, 13],
	optimizing: [14, 15],
};

const STATUS_MESSAGE_BY_VALUE = {
	starting: 'preparing',
	agent_preparing: 'preparing',
	agent_prepared: 'preparing',
	conversation_rounds_checking: 'preparing',
	conversation_rounds_checked: 'preparing',
	tuning_docs_retrieving: 'retrieving',
	tuning_docs_retrieved: 'retrieving',
	cache_checking: 'retrieving',
	cache_checked: 'retrieving',
	agent_invoking: 'thinking',
	agent_invoked: 'thinking',
	refining: 'thinking',
	refined: 'thinking',
	enriching_results: 'optimizing',
	results_enriched: 'optimizing',
};

const DEFAULT_BOT_TYPING_STATUS = '__default__';
const IGNORED_BOT_TYPING_MESSAGE = {
	contents: ['start', 'end'],
	statuses: ['agent-first-started', 'agent-flow-started', 'agent-first-end', 'agent-flow-end'],
};

// 後端新舊兩套狀態格式會並存，先統一成前端可顯示的文案 key。

const normalizeStatusCode = (status: any) => {
	if (typeof status === 'number') {
		return status;
	}

	if (typeof status === 'string' && status.trim() !== '') {
		const parsedStatus = Number(status);
		if (!Number.isNaN(parsedStatus)) {
			return parsedStatus;
		}
	}

	return null;
};

const normalizeStatusValue = (status: any) => {
	if (status === undefined || status === null) {
		return '';
	}

	return `${status}`.trim().toLowerCase();
};

const resolveLoadingStatusKey = (status: any) => {
	const normalizedStatus = normalizeStatusValue(status);

	if (normalizedStatus) {
		const matchedStatus = STATUS_MESSAGE_BY_VALUE[normalizedStatus as keyof typeof STATUS_MESSAGE_BY_VALUE];
		if (matchedStatus) {
			return matchedStatus;
		}
	}

	const normalizedStatusCode = normalizeStatusCode(status);
	if (normalizedStatusCode !== null) {
		if (LEGACY_STATUS_MESSAGE_GROUPS.preparing.includes(normalizedStatusCode)) {
			return 'preparing';
		}

		if (LEGACY_STATUS_MESSAGE_GROUPS.retrieving.includes(normalizedStatusCode)) {
			return 'retrieving';
		}

		if (LEGACY_STATUS_MESSAGE_GROUPS.thinking.includes(normalizedStatusCode)) {
			return 'thinking';
		}

		if (LEGACY_STATUS_MESSAGE_GROUPS.optimizing.includes(normalizedStatusCode)) {
			return 'optimizing';
		}
	}

	return null;
};

const getMessageStatus = (message: any) => {
	const status = message?.data?.status;

	if (status === undefined || status === null) {
		return null;
	}

	return `${status}`.trim() === '' ? null : status;
};

const isIgnoredBotTypingMessage = (message: any) => {
	return (
		IGNORED_BOT_TYPING_MESSAGE.contents.includes(message?.data?.content) ||
		IGNORED_BOT_TYPING_MESSAGE.statuses.includes(message?.data?.status)
	);
};

const isTypingMessage = (message: any) => {
	return message?.contentType === messageType.BOT_TYPING || message?.contentType === messageType.AGENT_PROCESSING;
};

const resolveTypingTimeout = (message: any) => {
	const rawDelay = message?.data?.delay;
	const parsedDelay = typeof rawDelay === 'number' ? rawDelay : Number(rawDelay);

	if (Number.isFinite(parsedDelay) && parsedDelay >= 0) {
		return parsedDelay;
	}

	return config.BOT_TYPING_TIMEOUT;
};

const useStyles = makeStyles(
	() => ({
		main: (props: any) => ({
			display: 'flex',
			flexDirection: 'column',
			borderRadius: props.isMobile ? 'none' : '12px',
			backgroundColor: color.$7,
			height: props.isMobile ? '100vh' : props.isExpanded ? '100%' : props?.windowSize?.height || 580,
			width: props.isMobile ? '100vw' : '100%',
			maxHeight: props.isMobile
				? 'none'
				: props.isExpanded
				? '100%'
				: `calc(100vh - 110px - ${props?.styleData?.positionSettings?.bottom || 0}px)`,
			fontSize: 'inherit',
		}),
		content: {
			height: '100%',
			display: 'flex',
			overflowY: 'auto',
			'-webkit-overflow-scrolling': 'touch',
			flexDirection: 'column-reverse',
		},
		contentContainer: {
			display: 'flex',
			flexDirection: 'column-reverse',
			justifyContent: 'flex-end',
			borderLeft: `1px solid ${color.$8}`,
			borderRight: `1px solid ${color.$8}`,
			height: 0,
			flex: 1,
		},
		me: {
			textAlign: 'right',
		},
		loading: {
			justifyContent: 'center',
			display: 'flex',
			alignItems: 'center',
			height: '100%',
		},
		header: (props: any) => ({
			position: 'relative',
			padding: 12,
			borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
			borderTopLeftRadius: props.isMobile ? 'none' : '12px',
			borderTopRightRadius: props.isMobile ? 'none' : '12px',
			minHeight: 51,
			display: 'flex',
			alignItems: 'center',
			backgroundColor: s8sColor.neutral.black,
		}),
		displayName: {
			fontSize: ({ fontScale }: any) => fontSize.md * (fontScale || 1),
			fontWeight: 'normal',
			fontStretch: 'normal',
			fontStyle: 'normal',
			lineHeight: '1',
			color: s8sColor.neutral.white,
		},
		x: {
			position: 'absolute',
			top: 12,
			right: 12,
			cursor: 'pointer',
		},
		expand: (props: any) => ({
			position: 'absolute',
			top: 12,
			right: props.alwaysOn ? 12 : 44,
			cursor: 'pointer',
		}),
		left: {
			position: 'relative',
			// bottom: 12,
			marginRight: 10,
			cursor: 'pointer',
		},
		fromMe: {
			display: 'flex',
			flexDirection: 'row-reverse',
			justifyContent: 'flex-start',
			alignItems: 'flex-start',
			padding: '12px 16px',
		},
		fromOrg: {
			display: 'flex',
			justifyContent: 'flex-start',
			alignItems: 'flex-start',
			padding: '12px 16px',
		},
		msgRow: {
			display: 'flex',
			alignItems: 'baseline',
			marginTop: 8,
		},
		datetime: {
			fontSize: ({ fontScale }: any) => fontSize.sm * (fontScale || 1),
			fontWeight: 'normal',
			fontStretch: 'normal',
			fontStyle: 'normal',
			lineHeight: '1.38',
			color: color.$10,
		},
		messageMe: {
			display: 'flex',
			justifyContent: 'flex-end',
			// width: '100%',
		},
		messageCustomer: {
			// display: 'flex',
			// width: '100%',
		},
		copyright: {
			fontSize: ({ fontScale }: any) => fontSize.sm * (fontScale || 1),
			fontWeight: 500,
			fontStretch: 'normal',
			fontStyle: 'normal',
			lineHeight: '1.38',
			color: color.$12,
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			marginBottom: '11px',
			'&:hover': {
				backgroundColor: 'rgba(224, 224, 224, 0.2)',
			},
			'& a': {
				color: s8sColor.neutral.gray800,
			},
		},
		error: {
			marginLeft: 6,
			fontSize: ({ fontScale }: any) => fontSize.sm * (fontScale || 1),
			color: color.$10,
		},
		errorRow: {
			display: 'flex',
		},
		headRow: {
			width: '68%',
		},
		richMenu: {
			width: 'calc(100% - 2px)',
			display: 'flex',
			borderLeft: `1px solid ${color.$9}`,
			borderRight: `1px solid ${color.$9}`,
		},
		richMenuItem: {
			cursor: 'pointer',
			fontSize: ({ fontScale }: any) => fontSize.sm * (fontScale || 1),
			lineHeight: 1.4,
			color: color.$12,
			borderTop: `1px solid ${color.$9}`,
			padding: '10px 8px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			wordBreak: 'break-all',
			textAlign: 'center',
		},
		hr: {
			width: 1,
			backgroundColor: color.$9,
		},
	}),
	{
		name: 'Chat',
	}
);

const Chat: ForwardRefRenderFunction<any, any> = (
	{ setIsOpenChat, onClose, onToggleExpand, orgData, styleData, isShowBottomInfo, isSuper8, alwaysOn, replyLocale }: any,
	forwardedRef
) => {
	const isMobile = useSelector((store: any) => store.socketio.isMobile);
	const isExpanded = useSelector((store: any) => store.layout?.isExpanded);

	const { windowSize, fontScale } = useResponsiveScale();

	const { t } = useTranslation();

	const classes = useStyles({ isMobile, styleData, windowSize, fontScale, isExpanded, alwaysOn });
	const [messages, setMessages] = useState<any>([]);
	const [isLoading, setIsloading] = useState(false);
	const [isLazyLoad, setIsLazyLoad] = useState(false);
	const [lastMessageLength, setLastMessageLength] = useState(0);
	const [botTypingStatus, setBotTypingStatus] = useState<any>(null);
	const [fileUrl, setFileUrl] = useState('');

	const messagesContainerRef: any = useRef(null);
	const messagesRef: any = useRef(null);
	const richMenuRef: any = useRef(null);
	const botTypingTimeoutRef: any = useRef(null);

	messagesRef.current = messages;

	const orgId = useSelector((store: any) => store.socketio.orgId);

	const socketClient = useSelector((store: any) => store.socketio.client);
	const socketClientReady = useSelector((store: any) => store.socketio.ready);

	const { livechatBackgroundColor, livechatTextColor, hiddenPoweredBy, richMenu } = styleData || {};
	const customerToken = useSelector((store: any) => store.socketio.customerToken);
	const canSendMessage = Boolean(customerToken && socketClient && socketClientReady);

	const [notifyMessage, setNotifyMessage] = useState<any>(null);

	const [botSetting, setBotSetting] = useState<any>({});

	const scrollEnd = () => {
		if (messagesContainerRef.current) {
			messagesContainerRef.current.scrollTop = -1;
		}

		setTimeout(() => {
			if (messagesContainerRef.current) {
				messagesContainerRef.current.scrollTop = 0;
			}
		}, 100);
	};

	const getMessage = async (limit = 16) => {
		const response: any = await LivechatService.getMessages(orgId, customerToken, messages.length, limit);

		setLastMessageLength(response.results.length);

		return response.results;
	};

	const getBotSettings = async (needSendMsg: boolean) => {
		const response: any = await LivechatService.getBotSetting(orgId, customerToken, needSendMsg);
		return response;
	};

	/**
	 * 重連後補回離線期間漏掉的訊息。
	 *
	 * 為什麼需要：outbound 是 socket 廣播到 customerId room，server 不檢查裡面有沒有人，訊息照樣被標成
	 * 已送出。手機切背景／鎖屏連線就斷，那段時間客服發的訊息會打進空房間；而歷史只在掛載時拉一次、
	 * 往上翻頁又只能拿更舊的，所以那些訊息在顧客端永久缺席（S8CO-55）。
	 */
	useEffect(() => {
		if (!socketClient || !customerToken) {
			return undefined;
		}

		const resync = createResync({
			// 讀 ref 而不是 state：這支函數的生命週期比一次 render 長。
			getMessages: () => messagesRef.current || [],
			fetchSince: (since: string, limit: number) => LivechatService.getMessagesSince(orgId, customerToken, since, limit),
			onMerged: (fetched: any[]) => {
				setMessages((currentMessages: any[]) => mergeMessages(currentMessages, fetched));
				scrollEnd();
			},
			onSettled: () => setMessages((currentMessages: any[]) => settlePendingMessages(currentMessages)),
			onError: (error: unknown) => logger.error('resync after reconnect failed', error),
		});

		const handleReconnect = () => {
			logger.log('reconnected, resyncing messages');
			resync();
		};
		// 掛在 manager（socketClient.io）上：`reconnect` 是 manager 的事件，不是 socket 的。
		socketClient.io?.on('reconnect', handleReconnect);
		return () => {
			socketClient.io?.off('reconnect', handleReconnect);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [socketClient, customerToken, orgId]);

	const addMessage = (messageObject: any) => {
		setMessages([messageObject, ...messagesRef.current]);
		scrollEnd();
	};

	useImperativeHandle(forwardedRef, () => ({
		notifyAddMessage(message: any) {
			setNotifyMessage(message);
		},
	}));

	useEffect(() => {
		if (!isLoading && notifyMessage) {
			// 以前這裡自己比 `receiptId`，沒做存在性檢查：歷史訊息都沒有 receiptId，所以
			// `undefined === undefined` 恆為真，這條路徑幾乎從不插入。改走共用規則。
			setMessages((currentMessages: any[]) => mergeMessage(currentMessages, notifyMessage));
			scrollEnd();
			setNotifyMessage(null);
		}
	}, [isLoading, notifyMessage]);

	const sendMessage = (messageObject: any, eventType: any = 'message') => {
		if (eventType === 'message' && !canSendMessage) {
			logger.warn('block send before identity ready', {
				eventType,
				hasClient: Boolean(socketClient),
				socketClientReady,
				customerToken,
			});
			return;
		}

		const payloadMessageObject = eventType === 'message' ? attachReplyLocale(messageObject, replyLocale) : messageObject;
		let data = JSON.stringify(payloadMessageObject);

		// sign the payload
		data = signPayload(data);

		emitOrQueueSocketEvent(socketClient, socketClientReady, eventType, data);
	};

	const submitMessage = async (sendData: any, type = messageType.TEXT_PLAIN_TYPE) => {
		if (!canSendMessage) {
			logger.warn('block submit before identity ready', {
				hasClient: Boolean(socketClient),
				socketClientReady,
				customerToken,
			});
			return;
		}

		const messageObject = await getMessageObject(sendData, type);
		addMessage(messageObject);
		sendMessage(messageObject);
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				setIsloading(true);
				const apiMessages = await getMessage();
				setMessages((currentMessages: any[]) => mergeMessages(currentMessages, apiMessages));
				const setting = await getBotSettings(false);
				setBotSetting(setting);
				scrollEnd();
			} catch (e) {
				logger.error('fetch chat data failed', e);
			} finally {
				setIsloading(false);
			}
		};
		if (customerToken) {
			fetchData();
		}
	}, [customerToken]);

	const getMessageStyle = (item: any) => {
		const resStyle: any = {};

		if (item.error || item.isPending) {
			resStyle.opacity = 0.5;
		}

		return resStyle;
	};

	const messageList = messages
		? messages.sort((a: any, b: any) => {
				const dateA: any = new Date(a.createdAt);
				const dateB: any = new Date(b.createdAt);
				return dateB - dateA;
		  })
		: [];

	const getLoadingStatusText = (status: any) => {
		const statusKey = resolveLoadingStatusKey(status);

		if (statusKey) {
			return t(`aiLoading.${statusKey}`);
		}

		if (status !== undefined && status !== null && `${status}`.trim() !== '') {
			return t('aiLoading.thinkingWithCode', { status });
		}

		return t('aiLoading.thinking');
	};

	const clearBotTypingTimeout = () => {
		if (botTypingTimeoutRef.current) {
			clearTimeout(botTypingTimeoutRef.current);
			botTypingTimeoutRef.current = null;
		}
	};

	const stopBotTyping = () => {
		clearBotTypingTimeout();
		setBotTypingStatus(null);
	};

	// socket typing 訊息只接受指定 content type，並在每次收到時刷新自動隱藏時間。
	const updateBotTypingStatus = (message: any) => {
		if (!isTypingMessage(message) || isIgnoredBotTypingMessage(message)) {
			return false;
		}

		const nextStatus = getMessageStatus(message);
		const nextTypingStatus = nextStatus !== null ? nextStatus : DEFAULT_BOT_TYPING_STATUS;
		const timeout = resolveTypingTimeout(message);

		clearBotTypingTimeout();
		setBotTypingStatus(nextTypingStatus);
		botTypingTimeoutRef.current = setTimeout(() => {
			setBotTypingStatus(null);
			botTypingTimeoutRef.current = null;
		}, timeout);

		return true;
	};

	const handleReceiveMessage = (message: any) => {
		if (updateBotTypingStatus(message)) {
			return;
		}

		if (isTypingMessage(message)) {
			return;
		}

		stopBotTyping();

		setMessages((currentMessages: any[]) => mergeMessage(currentMessages, message));
	};

	const onReceiveMessage = (message: any) => {
		if (permitMessageContentType(message.contentType)) {
			handleReceiveMessage(message);
			scrollEnd();
		}
	};

	const queryMessagesOfConversationLazy = async () => {
		const limit = 12;
		if (lastMessageLength >= limit) {
			const apiMessages = await getMessage(limit);
			setMessages((currentMessages: any[]) => mergeMessages(currentMessages, apiMessages));
		}
	};

	const handleScroll = async () => {
		if (
			!isLazyLoad &&
			messagesContainerRef.current.scrollHeight +
				messagesContainerRef.current.scrollTop -
				messagesContainerRef.current.clientHeight <
				10 &&
			messages &&
			messages.length > 0
		) {
			setIsLazyLoad(true);
			await queryMessagesOfConversationLazy();
			setIsLazyLoad(false);
		}
	};

	const onFileClick = (objectId: string, index: number = 0) => {
		const findFile = messages.find((d: any) => d.objectId === objectId);
		if (findFile) {
			if (findFile.contentType === messageType.IMAGE_SET) {
				setFileUrl(findFile?.data?.images?.[index]?.url);
			} else {
				setFileUrl(findFile.data.url);
			}
		}
	};

	const onEchoMessage = (message: any) => {
		if (message?.objectId) {
			sendMessage({ messageObjectId: message.objectId }, 'messageAck');
		}
	};

	const submitCustomerFormMessage = (data: any) => {
		setMessages([...messages.filter((d: any) => d.objectId !== data.objectId), data]);
	};

	const sendBotMessage = async () => {
		if (!canSendMessage) {
			logger.warn('block bot send before identity ready', {
				hasClient: Boolean(socketClient),
				socketClientReady,
				customerToken,
			});
			return;
		}

		const setting = await getBotSettings(true);
		if (setting.status === 1) {
			// 正常發送bot的keyword
			const messageObject = await getMessageObject({ content: botSetting.keyword }, messageType.TEXT_PLAIN_TYPE);
			sendMessage(messageObject);
		}
	};

	const richMenuComponent = useMemo(() => {
		const component: any[] | null = [];
		if (richMenu?.enabled) {
			richMenu?.buttons.forEach((menu: any, index: number) => {
				component.push(
					<div
						className={classes.richMenuItem}
						key={`buttons-${index}`}
						style={{
							width: `calc((100% - ${(richMenu?.buttons.length || 1) - 1}px) / ${richMenu?.buttons.length || 1})`,
						}}
						onClick={() => {
							switch (menu.type) {
								case 'postback':
									submitMessage({ content: menu?.data, type: 'postback', data: { t: 'richmenu', tg: menu?.tags.join('|') } });
									break;
								case 'url':
									window.open(menu.data);
									break;
								default:
							}
						}}
					>
						{menu?.title}
					</div>
				);

				if (richMenu?.buttons.length - 1 !== index) {
					component.push(<div key={`buttons-hr-${index}`} className={classes.hr} />);
				}
			});
			return component;
		}
		return null;
	}, [richMenu]);

	useEffect(() => {
		if (socketClient && socketClientReady) {
			socketClient.on('messageEcho', onReceiveMessage);
			socketClient.on('messageReceived', onReceiveMessage);
			socketClient.on('messageReceived', onEchoMessage);
		}
		return () => {
			if (socketClient && socketClientReady) {
				socketClient.off('messageEcho', onReceiveMessage);
				socketClient.off('messageReceived', onReceiveMessage);
				socketClient.off('messageReceived', onEchoMessage);
			}
		};
	}, [onReceiveMessage, onEchoMessage, socketClient, socketClientReady]);

	useEffect(() => {
		setMessages([]);
		stopBotTyping();
	}, [customerToken]);

	useEffect(() => {
		return () => {
			clearBotTypingTimeout();
		};
	}, []);

	return (
		<div className={classes.main}>
			<div className={classes.header} style={{ backgroundColor: livechatBackgroundColor }}>
				<IconBack id="left" onClick={() => setIsOpenChat(false)} className={classes.left} color={color.$7} />
				{!alwaysOn && <IconX id="x" onClick={() => onClose()} className={classes.x} color={color.$7} hoverColor={color.$7} />}
				{!isMobile && (
					<IconExpand
						id="expand"
						isExpanded={isExpanded}
						onClick={onToggleExpand}
						className={classes.expand}
						color={color.$7}
						hoverColor={color.$7}
					/>
				)}
				<ImageDisplay
					style={{
						borderRadius: '50%',
						marginRight: 12,
					}}
					src={orgData.icon}
					width={44}
					height={44}
				/>
				<div className={classes.headRow}>
					<div
						className={classes.displayName}
						style={{
							color: livechatTextColor,
						}}
					>
						{orgData.displayName}
					</div>
				</div>
			</div>
			{/* data-elastic：面板唯一可壓縮的區域。App 用「整體高度 − 這一塊」量出標題列、
			    rich menu、輸入列加起來不可壓縮的高度，當成面板的最低可用高度 */}
			<div className={classes.contentContainer} data-elastic>
				{isLoading ? (
					<div className={classes.loading}>
						<Circular topic="grey" variant="indeterminate" size={20} />
					</div>
				) : (
					<div className={classes.content} ref={messagesContainerRef} onScroll={handleScroll}>
						{!isSuper8 ? (
							isShowBottomInfo ? (
								<div className={classes.copyright}>
									<ImageDisplay
										style={{
											borderRadius: '50%',
											marginRight: 4,
										}}
										src={icSuperBotX}
										width={24}
										height={24}
									/>
									<a href={config.SUPERBOTX_URL} target="_blank" rel="noreferrer">
										{t('copyrigntSuperbotX')}
									</a>
								</div>
							) : null
						) : !hiddenPoweredBy ? (
							<div className={classes.copyright}>
								<ImageDisplay
									style={{
										borderRadius: '50%',
										marginRight: 4,
									}}
									src={icSuper8}
									width={24}
									height={24}
								/>
								<a href={config.SUPER8_URL} target="_blank" rel="noreferrer">
									{t('copyrignt')}
								</a>
							</div>
						) : null}
						{Boolean(botTypingStatus) && (
							<BotTyping
								orgData={orgData}
								text={botTypingStatus !== DEFAULT_BOT_TYPING_STATUS ? getLoadingStatusText(botTypingStatus) : undefined}
							/>
						)}
						{messageList.map((d: any, index: number) => {
							const fromMe = d.senderType === 'Customer' ? 'from-me' : '';

							return (
								<div className={`${fromMe ? classes.fromMe : classes.fromOrg} `} key={d.receiptId || d.objectId}>
									{!fromMe && (
										<ImageDisplay
											alt={d.receiptId || d.objectId}
											style={{
												borderRadius: '50%',
											}}
											src={orgData.icon}
											width={44}
											height={44}
										/>
									)}
									<div style={{ margin: '0px 12px', width: 'calc(100% - 88px)' }}>
										<div style={getMessageStyle(d)} className={!fromMe ? classes.messageCustomer : classes.messageMe}>
											<MessageContent
												styleData={styleData}
												replyLocale={replyLocale}
												onFileClick={onFileClick}
												fromMe={fromMe}
												data={d}
												submitCustomerFormMessage={submitCustomerFormMessage}
												submitMessage={submitMessage}
												isShowQuickReply={index === 0}
											/>
										</div>
										<div
											className={classes.msgRow}
											style={
												fromMe
													? {
															flexDirection: 'row-reverse',
													  }
													: {}
											}
										>
											{d.error || d.data.error ? (
												<div className={classes.errorRow}>
													<img src={icAlertTriangle} width={20} height={20} alt="alert" />
													<span className={classes.error}> {t('sendError')}</span>
												</div>
											) : (
												<span className={classes.datetime}>{moment(new Date(d.createdAt)).format('YYYY-MM-DD HH:mm')}</span>
											)}
										</div>
									</div>
								</div>
							);
						})}
						{isLazyLoad && (
							<div className={classes.loading}>
								<Circular topic="grey" variant="indeterminate" size={20} />
							</div>
						)}
					</div>
				)}
			</div>
			{richMenu?.enabled && (
				<div ref={richMenuRef} className={classes.richMenu}>
					{richMenuComponent}
				</div>
			)}
			<FileReviewer
				url={fileUrl}
				onCloseclick={() => {
					setFileUrl('');
				}}
			/>
			<ChatInput
				styleData={styleData}
				botSetting={botSetting}
				sendBotMessage={sendBotMessage}
				submitMessage={submitMessage}
				addMessage={addMessage}
				sendMessage={sendMessage}
				replyLocale={replyLocale}
				canSend={canSendMessage}
			/>
		</div>
	);
};

export default forwardRef(Chat);
